import { Component, ElementRef, HostListener, inject, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IdentityService } from '../../../../services/identity/identity.service';
import { UserTokenModel } from '../../../../models/auth/user-token-model';

@Component({
  selector: 'app-login-form',
  imports: [FormsModule],
  templateUrl: './login-form.html',
  styleUrl: './login-form.scss',
})
export class LoginForm implements OnInit {
  username = signal('');
  password = signal('');
  staySignedIn = signal(false);
  rememberMe = signal(false);
  loggingIn = signal(false);
  errorMessage = signal('');
  showPassword = signal(false);
  showAccountList = signal(false);

  /** Whether the app is running inside Electron. */
  isElectron = signal<boolean>(this.checkElectron());

  /** Remembered accounts loaded from localStorage. */
  rememberedAccounts = signal<{ username: string; password: string }[]>([]);

  changeView = output<'recover'>();

  private router = inject(Router);
  private identity = inject(IdentityService);
  private elRef = inject(ElementRef);

  /** Close the account dropdown when clicking outside the component. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.showAccountList.set(false);
    }
  }

  /** Close the account dropdown with a slight delay when the input loses focus. */
  onBlur() {
    setTimeout(() => {
      this.showAccountList.set(false);
    }, 150);
  }

  async ngOnInit(): Promise<void> {
    if (this.isElectron()) {
      await this.loadRememberedAccounts();
    }
  }

  /**
   * Load remembered accounts from the identity service.
   */
  private async loadRememberedAccounts() {
    const accounts = await this.identity.getRememberedAccounts();
    console.log("Our accounts are => ", accounts);
    this.rememberedAccounts.set(accounts);
  }

  /**
   * Called when the username input changes.
   * If the entered username matches a remembered account, auto-fill the password
   * and check the "Remember me" checkbox.
   */
  async onUsernameChange(value: string) {
    this.username.set(value);

    const match = this.rememberedAccounts().find(
      a => a.username.toLowerCase() === value.toLowerCase()
    );

    if (match) {
      this.password.set(match.password);
      this.rememberMe.set(true);
    }
  }

  /**
   * Called when a remembered account is selected from the dropdown.
   */
  selectAccount(account: { username: string; password: string }) {
    this.username.set(account.username);
    this.password.set(account.password);
    this.rememberMe.set(true);
    this.showAccountList.set(false);
  }

  /**
   * Login to service with credentials.
   */
  login() {
    if (this.username() && this.password()) {
      this.loggingIn.set(true);
      this.errorMessage.set('');

      // Create credential object to send for login.
      let credentials = {
        username: this.username(),
        password: this.password(),
        rememberMe: this.staySignedIn()
      };

      // Call login api.
      this.identity.login(credentials).subscribe({
        next: (response) => {
          // Handle remember / forget based on checkbox state (Electron only).
          if (this.isElectron()) {
            if (this.rememberMe()) {
              this.identity.rememberAccount(this.username(), this.password());
            } else {
              this.identity.forgetAccount(this.username());
            }
          }

          // Parse out the token.
          let base64Url = response.access_Token.split('.')[1];
          let decodedValue = JSON.parse(window.atob(base64Url));

          // Create a user token (temp)
          let userToken: UserTokenModel = new UserTokenModel();

          userToken.id = decodedValue.nameid;
          userToken.userName = decodedValue.unique_name;
          userToken.userNameSlug = userToken.userName.toLocaleLowerCase().replace(' ', '');
          userToken.token = response.access_Token;
          userToken.refreshToken = "";
          userToken.mfa = false;
          userToken.phone = "";
          userToken.email = decodedValue.email;

          console.log("Storing => ", userToken);
          this.identity.storeToken(userToken);

          this.router.navigate(['/home/overview']);
        },
        error: (error) => {
          console.error(error);
          if (error.status === 401) {
            this.errorMessage.set('Incorrect username or password.');
          } else {
            this.errorMessage.set('An error occurred during login. Please try again.');
          }
          this.loggingIn.set(false);
        },
        complete: () => {
          this.loggingIn.set(false);
        }
      });
    }
  }

  /**
   * Login to service with provider.
   * @param provider 
   */
  socialLogin(provider: string) {
    console.log(`Attempting to log in with ${provider}`);
  }

  private checkElectron(): boolean {
    return navigator.userAgent.toLowerCase().includes('electron');
  }
}
